import type { useAddAccount } from '@pages/addAccount/useAddAccount';
import { useMemo } from 'react';
import { useFixedFields } from './useFixedFields';
import { useFilterConfig } from './useFieldFilterConfig';
import { useOrderConfig } from './useOrderConfig';

export const useAllFieldsForms = (
    account: ReturnType<typeof useAddAccount>,
) => {
    const filterConfig = useFilterConfig();
    const { getFieldPosition } = useOrderConfig();
    const fixedFields = useFixedFields(account);

    const allFields = useMemo(() => {
        const {
            selectedRole,
            tipoContas,
            visibleFields,
            customFields,
            handleCustomFieldChange,
            setUserAlcada
        } = account;

        const {
            camposParaFiltrarPessoaFisica,
            camposParaFiltrarPessoaJuridica,
            camposParaFiltrarTransportadora,
            camposParaFiltrarPosto,
            camposParaFiltrarAdvogado,
            camposParaFiltrarProcon,
            camposParaFiltrarRevenda,
            camposSempreExcluir
        } = filterConfig;

        const accountTypes = {
            isTransportadora: selectedRole === 'transportadora',
            isPosto: selectedRole === 'posto',
            isAdvogado: selectedRole === 'advogado',
            isJuiz: selectedRole === 'juiz',
            isProcon: selectedRole === 'procon',
            isRevenda: selectedRole === 'revenda',
            isPessoaFisica: tipoContas === 'pessoa_fisica' && selectedRole !== 'procon',
            isPessoaJuridica: tipoContas === 'pessoa_juridica' &&
                !['transportadora', 'procon'].includes(selectedRole as string),
        };

        const formatFixedField = (field: any) => ({
            id: field.key,
            key: field.key,
            title: field.title,
            type: field.type,
            active: true,
            customField: false,
            value: field.value,
            onChange: field.onChange,
            error: field.error,
            existUser: field.existUser,
        });

        const shouldExcludeField = (fieldTitle: string): boolean => {
            if (fieldTitle.startsWith('Conciex MKT')) return true;
            if (camposSempreExcluir.some(excluded => fieldTitle.includes(excluded))) return true;


            const { isProcon, isRevenda, isTransportadora, isPosto, isAdvogado, isJuiz, isPessoaFisica, isPessoaJuridica } = accountTypes;

            const checkExclusion = (filterList: string[]): boolean =>
                filterList.some(prohibitedField => fieldTitle.includes(prohibitedField));

            if (isProcon) return checkExclusion(camposParaFiltrarProcon);
            if (isRevenda) return checkExclusion(camposParaFiltrarRevenda);
            if (isTransportadora) return checkExclusion(camposParaFiltrarTransportadora);
            if (isPosto) return checkExclusion(camposParaFiltrarPosto);
            if (isAdvogado || isJuiz) return checkExclusion(camposParaFiltrarAdvogado);
            if (isPessoaFisica) return checkExclusion(camposParaFiltrarPessoaFisica);
            if (isPessoaJuridica) return checkExclusion(camposParaFiltrarPessoaJuridica);
            if (isPessoaJuridica || isPessoaFisica) return checkExclusion(camposSempreExcluir);

            return false;
        };

        const formatDynamicField = (field: any) => {
            const value = field.type === 'checkbox'
                ? (typeof customFields[field.key] === 'string'
                    ? customFields[field.key] === 'true'
                    : Boolean(customFields[field.key]))
                : customFields[field.key];

            const onChange = (value: any) => {
                if (field.key === 'alcada_do_usuario') {
                    setUserAlcada(value as string);
                }
                handleCustomFieldChange(field.key, value);
            };

            return {
                ...field,
                customField: true,
                value,
                onChange,
            };
        };

        const fixedFieldsFormatted = fixedFields.map(formatFixedField);

        const dynamicFields = visibleFields
            .filter(field => field.active && !shouldExcludeField(field.title))
            .map(formatDynamicField);

        const combinedFields = [...fixedFieldsFormatted, ...dynamicFields];

        return combinedFields.sort((a, b) =>
            getFieldPosition(a.title, tipoContas, selectedRole) -
            getFieldPosition(b.title, tipoContas, selectedRole)
        );
    }, [
        account.visibleFields,
        account.customFields,
        account.tipoContas,
        account.selectedRole,
        filterConfig,
        fixedFields,
    ]);

    return allFields;
};